import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import apiClient from '../config/axios';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formatIndiaTime } from '../config/india-timezone';
import {
  MessageSquare,
  Users,
  FileText,
  Mail,
  TrendingUp,
  TrendingDown,
  Activity,
  Clock,
  Brain,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Dashboard = () => {
  const { currentWorkspace } = useWorkspace();
  
  const { data: dashboardData, isLoading, error } = useQuery(
    ['dashboard', currentWorkspace?.id],
    async () => {
      const response = await apiClient.get('/api/dashboard/overview', {
        headers: currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {}
      });
      return response.data;
    },
    {
      refetchInterval: 30000, // Refetch every 30 seconds
      enabled: !!currentWorkspace
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner w-8 h-8"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error loading dashboard data</p>
      </div>
    );
  }

  const overview = dashboardData?.overview || {};
  const recentMessages = dashboardData?.recentMessages || [];
  const dailyMessages = dashboardData?.dailyMessages || [];

  const StatCard = ({ title, value, icon: Icon, change, changeType = 'up' }) => (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mb-2">{value}</p>
          {change && (
            <div className="flex items-center">
              {changeType === 'up' ? (
                <TrendingUp className="w-4 h-4 text-green-500" />
              ) : changeType === 'down' ? (
                <TrendingDown className="w-4 h-4 text-red-500" />
              ) : null}
              <span className={`text-sm ml-1 font-medium ${
                changeType === 'up' ? 'text-green-600' : 
                changeType === 'down' ? 'text-red-600' : 
                'text-gray-600'
              }`}>
                {change}
              </span>
            </div>
          )}
        </div>
        <div className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  const RecentMessage = ({ message }) => (
    <div className="flex items-center space-x-4 p-4 hover:bg-gray-50 rounded-xl transition-all duration-200 border border-transparent hover:border-gray-200">
      <div className={`w-3 h-3 rounded-full ${
        message.status === 'sent' ? 'bg-green-500' :
        message.status === 'failed' ? 'bg-red-500' :
        'bg-yellow-500'
      }`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">
          {message.contact_name || message.to_number || message.from_number || 'Unknown'}
        </p>
        <p className="text-sm text-gray-600 truncate mt-1">
          {message.message_text}
        </p>
      </div>
      <div className="text-xs text-gray-400">
        {formatIndiaTime(message.created_at, 'time-ampm')}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Welcome back! Here's what's happening with your messages.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="WhatsApp Messages"
          value={overview.whatsappMessages || 0}
          icon={MessageSquare}
          change="+8%"
          changeType="up"
        />
        <StatCard
          title="Total Contacts"
          value={overview.totalContacts || 0}
          icon={Users}
          change="+5%"
          changeType="up"
        />
        <StatCard
          title="Templates"
          value={overview.totalTemplates || 0}
          icon={FileText}
        />
        <StatCard
          title="AI Uses"
          value={overview.aiUses || 0}
          icon={Brain}
          change={`${overview.aiTokens || 0} tokens`}
          changeType="neutral"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6">
        {/* Message Activity Chart */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
          <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <Activity className="w-5 h-5 mr-2 text-blue-600" />
            Message Activity (Last 7 Days)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dailyMessages}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="date" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

      </div>

      {/* Recent Activity and Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Messages */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
          <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-blue-600" />
              Recent Messages
            </h3>
          </div>
          <div className="p-6">
            {recentMessages.length > 0 ? (
              <div className="space-y-3">
                {recentMessages.slice(0, 5).map((message) => (
                  <RecentMessage key={message.id} message={message} />
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No recent messages</p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
          <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <Activity className="w-5 h-5 mr-2 text-blue-600" />
            Quick Actions
          </h3>
          <div className="space-y-4">
            <Link
              to="/chats"
              className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Send Message
            </Link>
            <Link
              to="/contacts"
              className="w-full flex items-center justify-center px-4 py-3 border-2 border-gray-200 text-gray-700 font-medium rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all duration-200"
            >
              <Users className="w-4 h-4 mr-2" />
              Add Contact
            </Link>
            <Link
              to="/templates"
              className="w-full flex items-center justify-center px-4 py-3 border-2 border-gray-200 text-gray-700 font-medium rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all duration-200"
            >
              <FileText className="w-4 h-4 mr-2" />
              Create Template
            </Link>
            <Link
              to="/email-campaigns"
              className="w-full flex items-center justify-center px-4 py-3 border-2 border-gray-200 text-gray-700 font-medium rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all duration-200"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Email
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
